import React, { useState, useEffect } from 'react'

import { useTheme } from '@material-ui/core'
import {
    FormControl,
    Select,
    MenuItem,
    Typography,
    Button,
    Popover,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    CircularProgress,
    Divider,
} from '@material-ui/core'
import {
    ArrowDropDown,
    Visibility,
    VisibilityOff,
    Schedule,
    Build,
    FormatPaint,
} from '@material-ui/icons'
import { ReactComponent as ClipboardTickSBIcon } from '../../assets/icons/clipboard-tick.svg'

import { Container } from '../../components'
import { Folder, UnitVisibility, traverse } from '../../models'
import { FinderFolder, FinderItem } from '../../components/Finder'

interface Props {
    root?: Folder
    updateUnitVisibility: (unitId: number, visibility: UnitVisibility) => void
    bulkUpdateUnitVisibility: (
        unitIds: number[],
        visibility: UnitVisibility,
    ) => Promise<void>
}

interface FolderOpenState {
    [folderId: number]: boolean
}

export const UnitVisibilityManager: React.FC<Props> = ({
    root,
    updateUnitVisibility,
    bulkUpdateUnitVisibility,
}) => {
    const theme = useTheme()
    const [openState, setOpenState] = useState<FolderOpenState>({})
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null)
    const [hoveredFolderId, setHoveredFolderId] = useState<number | null>(null)
    const [
        selectedVisibility,
        setSelectedVisibility,
    ] = useState<UnitVisibility | null>(null)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [isUpdating, setIsUpdating] = useState(false)

    useEffect(() => {
        if (root) {
            const newOpenState: FolderOpenState = {}
            traverse(root, (folder) => {
                newOpenState[folder.id] = openState[folder.id] ?? !folder.parent
            })
            if (JSON.stringify(newOpenState) !== JSON.stringify(openState)) {
                setOpenState(newOpenState)
            }
        }
    }, [root])

    const getFolderUnitIds = (folder: Folder): number[] => {
        let unitIds: number[] = [...folder.units.map((unit) => unit.id)]
        folder.children.forEach((child) => {
            unitIds = [...unitIds, ...getFolderUnitIds(child)]
        })
        return unitIds
    }

    const getFolderVisibilitySummary = (
        folder: Folder,
    ): { [key in UnitVisibility]: number } => {
        const summary = {
            [UnitVisibility.NOT_VISIBLE]: 0,
            [UnitVisibility.SCHEDULE_ONLY]: 0,
            [UnitVisibility.INSPECTION_ONLY]: 0,
            [UnitVisibility.ALWAYS_VISIBLE]: 0,
        }

        const updateSummary = (f: Folder) => {
            f.units.forEach((unit) => {
                summary[unit.visibility]++
            })
            f.children.forEach(updateSummary)
        }

        updateSummary(folder)
        return summary
    }

    const handleBulkUpdateClick = (
        event: React.MouseEvent<HTMLElement>,
        folder: Folder,
    ) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget)
        setSelectedFolder(folder)
    }

    const handleVisibilitySelect = (
        event: React.MouseEvent<HTMLElement>,
        visibility: UnitVisibility,
    ) => {
        event.stopPropagation()
        setSelectedVisibility(visibility)
        setAnchorEl(null)
        setConfirmOpen(true)
    }

    const handleClosePopover = (event?: any) => {
        if (event && event.stopPropagation) {
            event.stopPropagation()
        }
        setAnchorEl(null)
    }

    const handleCloseDialog = (event?: any, reason?: string) => {
        if (event && event.stopPropagation) {
            event.stopPropagation()
        }
        setConfirmOpen(false)
        setSelectedFolder(null)
        setSelectedVisibility(null)
    }

    const handleConfirmBulkUpdate = async () => {
        if (selectedFolder && selectedVisibility) {
            setIsUpdating(true)
            try {
                const unitIds = getFolderUnitIds(selectedFolder)
                await bulkUpdateUnitVisibility(unitIds, selectedVisibility)
            } finally {
                setIsUpdating(false)
                handleCloseDialog()
            }
        }
    }

    const getVisibilityIcon = (visibility: UnitVisibility) => {
        switch (visibility) {
            case UnitVisibility.NOT_VISIBLE:
                return <VisibilityOff fontSize="small" />
            case UnitVisibility.SCHEDULE_ONLY:
                return <FormatPaint fontSize="small" />
            case UnitVisibility.INSPECTION_ONLY:
                return <ClipboardTickSBIcon style={{ width: 20, height: 20 }} />
            case UnitVisibility.ALWAYS_VISIBLE:
                return <Visibility fontSize="small" />
        }
    }

    const getFolderUpdateButton = (folder: Folder) => {
        const folderUnitIds = getFolderUnitIds(folder)
        if (folderUnitIds.length === 0) return <Container />

        return (
            <Tooltip title="Update visibility for all units in this folder and its subfolders">
                <Button
                    size="small"
                    variant="outlined"
                    endIcon={<ArrowDropDown />}
                    onClick={(e) => {
                        e.stopPropagation()
                        handleBulkUpdateClick(e, folder)
                    }}
                    disabled={isUpdating}
                    style={{ minWidth: '180px' }}
                >
                    Update {folderUnitIds.length} Units
                </Button>
            </Tooltip>
        )
    }

    const renderedTree: JSX.Element[] = []

    const renderFolder = (folder: Folder, level = 0) => {
        const folderUnitIds = getFolderUnitIds(folder)
        const visibilitySummary = getFolderVisibilitySummary(folder)

        renderedTree.push(
            <FinderItem
                level={level}
                key={`FINDER_FOLDER_${folder.id}`}
                myKey={`FINDER_FOLDER_${folder.id}`}
                selected={false}
                selectedIcon={getFolderUpdateButton(folder)}
            >
                <Container
                    alignItems="center"
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Container alignItems="center">
                        <FinderFolder
                            folder={folder}
                            open={openState[folder.id]}
                            onClick={(e) => {
                                e.stopPropagation()
                                setOpenState({
                                    ...openState,
                                    [folder.id]: !openState[folder.id],
                                })
                            }}
                        />
                        {folderUnitIds.length > 0 && (
                            <Container
                                alignItems="center"
                                style={{ marginLeft: theme.spacing(2) }}
                            >
                                {Object.entries(visibilitySummary).map(
                                    ([visibility, count]) =>
                                        count > 0 && (
                                            <Container
                                                key={`FOLDER_${folder.id}_${visibility}`}
                                                alignItems="center"
                                                style={{
                                                    marginRight: theme.spacing(
                                                        1,
                                                    ),
                                                    cursor: 'help',
                                                }}
                                            >
                                                <Tooltip
                                                    title={`${count} units ${visibility
                                                        .toLowerCase()
                                                        .replace('_', ' ')}`}
                                                >
                                                    {getVisibilityIcon(
                                                        visibility as UnitVisibility,
                                                    )}
                                                </Tooltip>
                                                <Typography
                                                    variant="caption"
                                                    style={{
                                                        marginLeft: 4,
                                                    }}
                                                >
                                                    {count}
                                                </Typography>
                                            </Container>
                                        ),
                                )}
                            </Container>
                        )}
                    </Container>
                </Container>
            </FinderItem>,
        )

        if (openState[folder.id]) {
            if (folder.units.length > 0) {
                renderedTree.push(
                    <Divider
                        key={`DIVIDER_${folder.id}`}
                        style={{ margin: theme.spacing(1, 0) }}
                    />,
                )
            }

            folder.children.forEach((child) => {
                renderFolder(child, level + 1)
            })

            folder.units.forEach((unit) => {
                renderedTree.push(
                    <FinderItem
                        level={level + 1}
                        key={`FINDER_UNIT_${unit.id}`}
                        myKey={`FINDER_UNIT_${unit.id}`}
                        selected={false}
                        selectedIcon={<Container />}
                    >
                        <Container alignItems="center">
                            <Typography
                                variant="body2"
                                style={{ marginRight: theme.spacing(2) }}
                            >
                                {unit.name}
                            </Typography>
                            <FormControl size="small" style={{ width: 200 }}>
                                <Select
                                    value={unit.visibility}
                                    onChange={(e) => {
                                        updateUnitVisibility(
                                            unit.id,
                                            e.target.value as UnitVisibility,
                                        )
                                    }}
                                >
                                    <MenuItem
                                        value={UnitVisibility.NOT_VISIBLE}
                                    >
                                        <Container alignItems="center">
                                            <VisibilityOff
                                                fontSize="small"
                                                style={{
                                                    marginRight: theme.spacing(
                                                        1,
                                                    ),
                                                }}
                                            />
                                            Not Visible
                                        </Container>
                                    </MenuItem>
                                    <MenuItem
                                        value={UnitVisibility.SCHEDULE_ONLY}
                                    >
                                        <Container alignItems="center">
                                            <Schedule
                                                fontSize="small"
                                                style={{
                                                    marginRight: theme.spacing(
                                                        1,
                                                    ),
                                                }}
                                            />
                                            Schedule Only
                                        </Container>
                                    </MenuItem>
                                    <MenuItem
                                        value={UnitVisibility.INSPECTION_ONLY}
                                    >
                                        <Container alignItems="center">
                                            <Build
                                                fontSize="small"
                                                style={{
                                                    marginRight: theme.spacing(
                                                        1,
                                                    ),
                                                }}
                                            />
                                            Inspection Only
                                        </Container>
                                    </MenuItem>
                                    <MenuItem
                                        value={UnitVisibility.ALWAYS_VISIBLE}
                                    >
                                        <Container alignItems="center">
                                            <Visibility
                                                fontSize="small"
                                                style={{
                                                    marginRight: theme.spacing(
                                                        1,
                                                    ),
                                                }}
                                            />
                                            Always Visible
                                        </Container>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Container>
                    </FinderItem>,
                )
            })
        }
    }

    if (root) {
        renderFolder(root, 0)
    }

    return (
        <>
            <Container
                direction="column"
                flex={1}
                style={{
                    border: `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: theme.shape.borderRadius,
                    padding: theme.spacing(1),
                    height: '100%',
                }}
                scrollY
            >
                {renderedTree}
            </Container>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <Container
                    direction="column"
                    style={{ padding: theme.spacing(1) }}
                >
                    <MenuItem
                        onClick={(e) =>
                            handleVisibilitySelect(
                                e,
                                UnitVisibility.NOT_VISIBLE,
                            )
                        }
                    >
                        <Container alignItems="center">
                            <VisibilityOff
                                fontSize="small"
                                style={{ marginRight: theme.spacing(1) }}
                            />
                            Not Visible
                        </Container>
                    </MenuItem>
                    <MenuItem
                        onClick={(e) =>
                            handleVisibilitySelect(
                                e,
                                UnitVisibility.SCHEDULE_ONLY,
                            )
                        }
                    >
                        <Container alignItems="center">
                            <Schedule
                                fontSize="small"
                                style={{ marginRight: theme.spacing(1) }}
                            />
                            Schedule Only
                        </Container>
                    </MenuItem>
                    <MenuItem
                        onClick={(e) =>
                            handleVisibilitySelect(
                                e,
                                UnitVisibility.INSPECTION_ONLY,
                            )
                        }
                    >
                        <Container alignItems="center">
                            <Build
                                fontSize="small"
                                style={{ marginRight: theme.spacing(1) }}
                            />
                            Inspection Only
                        </Container>
                    </MenuItem>
                    <MenuItem
                        onClick={(e) =>
                            handleVisibilitySelect(
                                e,
                                UnitVisibility.ALWAYS_VISIBLE,
                            )
                        }
                    >
                        <Container alignItems="center">
                            <Visibility
                                fontSize="small"
                                style={{ marginRight: theme.spacing(1) }}
                            />
                            Always Visible
                        </Container>
                    </MenuItem>
                </Container>
            </Popover>

            <Dialog
                open={confirmOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                onClick={(e) => e.stopPropagation()}
            >
                <DialogTitle>Confirm Bulk Update</DialogTitle>
                <DialogContent>
                    <Container
                        direction="column"
                        style={{ gap: theme.spacing(2) }}
                    >
                        <Typography>
                            Are you sure you want to update{' '}
                            {selectedFolder
                                ? getFolderUnitIds(selectedFolder).length
                                : 0}{' '}
                            units to be{' '}
                            {selectedVisibility
                                ?.toLowerCase()
                                .replace('_', ' ')}
                            ?
                        </Typography>
                        {selectedFolder && (
                            <Container
                                direction="column"
                                style={{ gap: theme.spacing(1) }}
                            >
                                <Typography variant="subtitle2">
                                    Current visibility distribution:
                                </Typography>
                                <Container
                                    direction="column"
                                    style={{ gap: theme.spacing(0.5) }}
                                >
                                    {Object.entries(
                                        getFolderVisibilitySummary(
                                            selectedFolder,
                                        ),
                                    ).map(
                                        ([visibility, count]) =>
                                            count > 0 && (
                                                <Container
                                                    key={visibility}
                                                    alignItems="center"
                                                >
                                                    {getVisibilityIcon(
                                                        visibility as UnitVisibility,
                                                    )}
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            marginLeft: theme.spacing(
                                                                1,
                                                            ),
                                                        }}
                                                    >
                                                        {count} units{' '}
                                                        {visibility
                                                            .toLowerCase()
                                                            .replace('_', ' ')}
                                                    </Typography>
                                                </Container>
                                            ),
                                    )}
                                </Container>
                            </Container>
                        )}
                    </Container>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmBulkUpdate}
                        color="primary"
                        variant="contained"
                        disabled={isUpdating}
                        startIcon={
                            isUpdating ? <CircularProgress size={20} /> : null
                        }
                    >
                        {isUpdating ? 'Updating...' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
